import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { LABELS } from "@match/shared";
import { PageHeader, EmptyState } from "@/components/Layout";
import { DeleteForever } from "@/components/delete";
import { NewPropAccountPanel } from "@/components/prop-onboarding";
import { PayoutsSection } from "@/components/payouts";
import { PeriodsSection } from "@/components/periods";
import { RebalanceSection } from "@/components/rebalance";
import { DataTable, type DataTableColumn } from "@/components/table";
import { HoldNote, StateBadge, PHASE_LABEL } from "@/components/session";
import { Button, Dash, ErrorNote, Hint, inputClass, KeyFigures, useCanWrite, useIsClient } from "@/components/ui";
import { errorMessage } from "@/lib/api";
import { date, money, percent, signClass } from "@/lib/format";
import { useAccountGroups, useCycle, useCycles, usePropFirms } from "@/lib/queries";
import type { CycleListItem } from "@/lib/types";

/*
 * Cicli operativi.
 *
 * Il ciclo è l'unità economica del modello: nasce con la fee, attraversa le
 * fasi e chiude alla bruciatura del conto prop. Fin qui lo si vedeva solo di
 * sbieco — dentro la scheda di un cliente, o come riga di una fase — e per
 * sapere a che punto fosse bisognava sapere di chi era.
 *
 * Qui non si calcola niente: sono le colonne del ciclo, nella valuta del conto
 * prop. «Fasi» resta la pagina di chi opera; questa è la pagina di chi guarda
 * come stanno andando i cicli.
 *
 * **La vista del CLIENT non cambia**: resta la tabella e le tre schede di
 * prima (`ClientCyclesView`). Il riordino — KeyFigures, filtri a pulsanti,
 * DataTable ordinabile — è solo per chi opera (`OperatorCyclesView`).
 */

export function Cycles() {
  const isClient = useIsClient();
  return isClient ? <ClientCyclesView /> : <OperatorCyclesView />;
}

// ---------------------------------------------------------------------------
// Vista del CLIENT: invariata
// ---------------------------------------------------------------------------

const TABS = [
  { key: "vivi", label: "Attivi", states: ["ACTIVE"] },
  { key: "chiusi", label: "Chiusi", states: ["CLOSED_PROFIT", "CLOSED_LOSS", "ABANDONED"] },
  { key: "tutti", label: "Tutti", states: [] as string[] },
] as const;

function ClientCyclesView() {
  const isClient = useIsClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = TABS.find((t) => t.key === params.get("stato")) ?? TABS[0];
  const cycles = useCycles({ state: [...tab.states] });

  return (
    <>
      <PageHeader title="Cicli operativi" subtitle="Un ciclo per cliente: fee, fasi, recover, e come è finito" />

      <div className="border-b border-line px-6">
        <nav className="flex gap-1" aria-label="Stato dei cicli">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => {
                const next = new URLSearchParams(params);
                if (t.key === "vivi") next.delete("stato");
                else next.set("stato", t.key);
                setParams(next, { replace: true });
              }}
              aria-current={t.key === tab.key ? "page" : undefined}
              className={[
                "-mb-px border-b-2 px-3 py-2.5 text-sm transition-colors",
                t.key === tab.key ? "border-ink font-medium text-ink" : "border-transparent text-muted hover:text-ink",
              ].join(" ")}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {cycles.isLoading ? (
        <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>
      ) : cycles.error ? (
        <div className="px-6 py-6">
          <ErrorNote>{errorMessage(cycles.error)}</ErrorNote>
        </div>
      ) : !cycles.data?.length ? (
        <EmptyState
          title={tab.key === "vivi" ? "Nessun ciclo attivo" : "Nessun ciclo"}
          hint={
            isClient
              ? "Qui compaiono i tuoi cicli, con quanto è stato recuperato e quanto manca."
              : "Un ciclo nasce con la prima fase di un cliente: aprila da Fasi, e comparirà qui con la sua fee e il suo recover."
          }
          // `ClientCyclesView` è solo del CLIENT (`isClient` è sempre vero qui):
          // per lui non c'è azione, il ciclo nasce dalla scheda dell'operatore.
          action={undefined}
        />
      ) : (
        <div className="relative overflow-x-auto px-6 py-4">
          {/* `relative`: lo `sr-only` delle Azioni è assoluto e senza questo esce dal riquadro, allargando la pagina da cellulare. */}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs text-faint">
                <th className="py-2 pr-4 font-medium">Cliente</th>
                <th className="py-2 pr-4 font-medium">Prop firm</th>
                <th className="py-2 pr-4 font-medium">Fase</th>
                <th className="py-2 pr-4 font-medium">Stato</th>
                <th className="py-2 pr-4 text-right font-medium">{LABELS.terms.recoverAmount}</th>
                <th className="py-2 pr-4 text-right font-medium" title="Com'è andato il ciclo: solo sui cicli chiusi">Netto</th>
                <th className="py-2 pr-4 text-right font-medium">{LABELS.terms.feesPaid}</th>
                <th className="py-2 pr-4 text-right font-medium">{LABELS.terms.realRealizedPnl}</th>
                <th className="py-2 pr-4 text-right font-medium">Giorni</th>
                <th className="py-2 font-medium">
                  <span className="sr-only">Azioni</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {cycles.data.map((c) => (
                <tr key={c.id} className="border-b border-line/60 hover:bg-raised/40">
                  <td className="py-2.5 pr-4">
                    <Link to={`/cicli/${c.id}`} className="font-medium text-ink hover:underline">
                      {c.client.name}
                    </Link>
                    {c.label && <span className="ml-2 text-muted">{c.label}</span>}
                  </td>
                  <td className="py-2.5 pr-4">
                    <span className="text-ink">{c.propFirm.name}</span>{" "}
                    <span className="text-muted">{money(c.accountSize, c.currency)}</span>
                  </td>
                  <td className="py-2.5 pr-4 text-muted">{PHASE_LABEL(c.currentPhase)}</td>
                  <td className="py-2.5 pr-4">
                    <StateBadge kind="cycle" state={c.state} />
                    {/* Il ciclo e' fermo perche' lo e' la sua Fase viva: qui si
                        vede che quel cliente non sta operando, e da quando. */}
                    {c.hold && <HoldNote hold={{ reason: c.hold.reason, note: c.hold.note, since: c.hold.since }} className="mt-1 block" />}
                  </td>
                  {/* Su un ciclo chiuso il recover e' storia, non un obiettivo:
                      si scrive in grigio e col titolo che lo dice. */}
                  <td className={`py-2.5 pr-4 text-right ${c.closedAt ? "text-faint" : "text-ink"}`} title={c.closedAt ? "Congelato alla chiusura del ciclo" : undefined}>
                    {money(c.recoverAmount, c.currency)}
                  </td>
                  <td className={`py-2.5 pr-4 text-right ${c.cycleNet === null ? "text-faint" : signClass(Number(c.cycleNet))}`}>
                    {c.cycleNet === null ? "—" : money(c.cycleNet, c.currency)}
                  </td>
                  <td className="py-2.5 pr-4 text-right text-muted">{money(c.feesPaid, c.currency)}</td>
                  <td className={`py-2.5 pr-4 text-right ${signClass(Number(c.realRealizedPnl))}`}>{money(c.realRealizedPnl, c.currency)}</td>
                  <td className="py-2.5 pr-4 text-right text-muted" title={`Aperto il ${date(c.openedAt)}${c.closedAt ? `, chiuso il ${date(c.closedAt)}` : ""}`}>
                    {c.daysOpen}
                  </td>
                  <td className="py-2.5">
                    <div className="flex justify-end">
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/cicli/${c.id}`)}>
                        Apri
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Vista di chi opera: numeri chiave, filtri a pulsanti, tabella ordinabile
// ---------------------------------------------------------------------------

type StatoFiltro = "TUTTI" | "ATTIVO" | "IN_ATTESA" | "CHIUSO";

const STATO_OPZIONI: { key: StatoFiltro; label: string }[] = [
  { key: "TUTTI", label: "Tutti" },
  { key: "ATTIVO", label: "Attivo" },
  { key: "IN_ATTESA", label: "In attesa del conto" },
  { key: "CHIUSO", label: "Chiuso" },
];

const PHASE_RANK: Record<string, number> = { PHASE_1: 1, PHASE_2: 2, FUNDED: 3 };

/** Pulsanti mutuamente esclusivi: non una tendina, e nessuna selezione multipla da tenere allineata. */
function FilterButtons<T extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { key: T; label: string }[];
  label: string;
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-1 rounded border border-line bg-raised p-1">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          aria-pressed={value === o.key}
          onClick={() => onChange(o.key)}
          className={[
            "rounded px-2.5 py-1 text-xs font-medium transition-colors",
            value === o.key ? "bg-surface text-ink shadow-sm" : "text-muted hover:text-ink",
          ].join(" ")}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Somma per valuta: importi di valute diverse non si sommano in un solo numero (vedi `ledger.tsx`). */
function sumByCurrency(rows: { currency: string; value: number }[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const r of rows) out[r.currency] = (out[r.currency] ?? 0) + r.value;
  return out;
}

function CurrencyTotal({ totals }: { totals: Record<string, number> }) {
  const entries = Object.entries(totals);
  if (!entries.length) return <Dash />;
  return (
    <>
      {entries.map(([cur, v], i) => (
        <span key={cur}>
          {i > 0 && " · "}
          {money(v, cur)}
        </span>
      ))}
    </>
  );
}

function sameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

/** Giorni interi da un istante passato: per «in attesa del conto · da N giorni». */
function daysSince(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

function OperatorCyclesView() {
  const navigate = useNavigate();
  const cycles = useCycles({});
  const propFirms = usePropFirms();
  const groups = useAccountGroups();
  const [stato, setStato] = useState<StatoFiltro>("ATTIVO");
  const [propFirmId, setPropFirmId] = useState("");
  const [search, setSearch] = useState("");
  /** Il ciclo in attesa per cui si sta registrando il conto prop arrivato. */
  const [registraFor, setRegistraFor] = useState<CycleListItem | null>(null);

  const all = useMemo(() => cycles.data ?? [], [cycles.data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((c) => {
      if (stato === "ATTIVO" && !(c.state === "ACTIVE" && !c.awaitingPropPhase)) return false;
      if (stato === "IN_ATTESA" && !c.awaitingPropPhase) return false;
      if (stato === "CHIUSO" && c.state === "ACTIVE") return false;
      if (propFirmId && c.propFirm.id !== propFirmId) return false;
      if (q && !c.client.name.toLowerCase().includes(q) && !(c.label ?? "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [all, stato, propFirmId, search]);

  // Le KeyFigures guardano TUTTI i cicli, non il filtro applicato alla tabella:
  // sono la vista d'insieme da cui i filtri fanno drill-down.
  const attivi = all.filter((c) => c.state === "ACTIVE");
  const daRecuperare = sumByCurrency(attivi.map((c) => ({ currency: c.currency, value: Number(c.recoverAmount) })));
  const feePagate = sumByCurrency(all.map((c) => ({ currency: c.currency, value: Number(c.feesPaid) })));
  const ora = new Date();
  const nettoDelMese = sumByCurrency(
    all
      .filter((c) => c.closedAt && c.cycleNet !== null && sameMonth(new Date(c.closedAt), ora))
      .map((c) => ({ currency: c.currency, value: Number(c.cycleNet) })),
  );

  const columns: DataTableColumn<CycleListItem>[] = [
    {
      key: "client",
      header: "Cliente",
      cell: (c) => <span className="font-medium text-ink">{c.client.name}</span>,
      sortBy: (c) => c.client.name.toLowerCase(),
    },
    { key: "label", header: "Ciclo", cell: (c) => c.label ?? <Dash />, sortBy: (c) => (c.label ?? "").toLowerCase() },
    {
      key: "firm",
      header: "Firm e programma",
      cell: (c) => (
        <>
          <span className="text-ink">{c.propFirm.name}</span> <span className="text-muted">{c.preset.name}</span>
        </>
      ),
      sortBy: (c) => `${c.propFirm.name} ${c.preset.name}`.toLowerCase(),
    },
    { key: "size", header: "Taglia", align: "right", cell: (c) => money(c.accountSize, c.currency), sortBy: (c) => Number(c.accountSize) },
    {
      key: "phase",
      header: "Fase corrente",
      cell: (c) => {
        const giorni = c.awaitingPropSince ? daysSince(c.awaitingPropSince) : 0;
        return (
          <div>
            <span className="text-ink">{PHASE_LABEL(c.currentPhase)}</span> <StateBadge kind="cycle" state={c.state} />
            {c.awaitingPropPhase ? (
              <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-warn">
                in attesa del conto · da {giorni} {giorni === 1 ? "giorno" : "giorni"}
                <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); setRegistraFor(c); }}>
                  Registra il conto
                </Button>
              </span>
            ) : (
              c.hold && <HoldNote hold={{ reason: c.hold.reason, note: c.hold.note, since: c.hold.since }} className="mt-0.5 block" />
            )}
          </div>
        );
      },
      sortBy: (c) => PHASE_RANK[c.currentPhase] ?? 0,
    },
    {
      key: "recover",
      header: LABELS.terms.recoverAmount,
      align: "right",
      cell: (c) => (
        <span className={c.closedAt ? "text-faint" : "text-ink"} title={c.closedAt ? "Congelato alla chiusura del ciclo" : undefined}>
          {money(c.recoverAmount, c.currency)}
        </span>
      ),
      sortBy: (c) => Number(c.recoverAmount),
    },
    { key: "fees", header: LABELS.terms.feesPaid, align: "right", cell: (c) => money(c.feesPaid, c.currency), sortBy: (c) => Number(c.feesPaid) },
    {
      key: "propResets",
      header: "Reset",
      align: "right",
      cell: (c) => (c.propResets ? c.propResets : <Dash />),
      sortBy: (c) => c.propResets ?? 0,
    },
    {
      key: "closedPeriods",
      header: "Perdite fasi precedenti",
      align: "right",
      cell: (c) => {
        if (!c.closedPeriods.length) return <Dash />;
        const noti = c.closedPeriods.filter((p) => p.netCycle !== null);
        // Almeno un periodo senza netto: la somma non si può dare per certa, e lo dice con "≈".
        const incompleto = noti.length !== c.closedPeriods.length;
        if (!noti.length) return <Dash title="Nessun periodo con netto scritto" />;
        const somma = noti.reduce((s, p) => s + Number(p.netCycle), 0);
        return (
          <span className="inline-flex items-center gap-1">
            <span className={signClass(somma)}>
              {incompleto ? "≈ " : ""}
              {money(somma, c.currency)}
            </span>
            <Hint label="Dettaglio per fase">
              {c.closedPeriods.map((p) => (
                <span key={p.id} className="block">
                  {LABELS.cyclePhase[p.phase]}: {p.netCycle === null ? "— (netto non scritto)" : money(p.netCycle, c.currency)}
                </span>
              ))}
            </Hint>
          </span>
        );
      },
      sortBy: (c) => {
        const noti = c.closedPeriods.filter((p) => p.netCycle !== null);
        return noti.length ? noti.reduce((s, p) => s + Number(p.netCycle), 0) : null;
      },
    },
    {
      key: "payouts",
      header: "Payout incassati",
      align: "right",
      cell: (c) => money(c.payoutsReceived, c.currency),
      sortBy: (c) => Number(c.payoutsReceived),
    },
    {
      key: "net",
      header: "Netto",
      align: "right",
      cell: (c) =>
        c.cycleNet === null ? (
          <Dash />
        ) : (
          <>
            <span className={signClass(Number(c.cycleNet))}>{money(c.cycleNet, c.currency)}</span>
            {/* «Di cui ribilanciamenti»: arriva solo all'operatore. */}
            {c.cycleNetRebalances != null && Number(c.cycleNetRebalances) !== 0 && (
              <span className="block text-xs text-faint">di cui ribil. {money(c.cycleNetRebalances, c.currency)}</span>
            )}
          </>
        ),
      sortBy: (c) => (c.cycleNet === null ? null : Number(c.cycleNet)),
    },
    {
      key: "days",
      header: "Aperto da",
      align: "right",
      cell: (c) => <span title={`Aperto il ${date(c.openedAt)}${c.closedAt ? `, chiuso il ${date(c.closedAt)}` : ""}`}>{c.daysOpen}</span>,
      sortBy: (c) => c.daysOpen,
    },
  ];

  return (
    <>
      <PageHeader title="Cicli operativi" subtitle="Un ciclo per cliente: fee, fasi, recover, e come è finito" />

      <div className="space-y-4 px-6 py-5">
        <KeyFigures
          items={[
            { label: "Cicli attivi", value: attivi.length },
            {
              label: `${LABELS.terms.recoverAmount} totale`,
              value: <CurrencyTotal totals={daRecuperare} />,
              hint: "Somma sui cicli attivi. Valute diverse restano separate: non c'è un cambio da applicare qui.",
            },
            { label: `${LABELS.terms.feesPaid} totali`, value: <CurrencyTotal totals={feePagate} />, hint: "Su tutti i cicli, aperti e chiusi." },
            { label: "Netto cicli chiusi del mese", value: <CurrencyTotal totals={nettoDelMese} />, hint: "Cicli chiusi nel mese solare corrente." },
          ]}
        />

        <div className="flex flex-wrap items-center gap-3">
          <FilterButtons label="Stato del ciclo" value={stato} onChange={setStato} options={STATO_OPZIONI} />
          <FilterButtons
            label="Prop firm"
            value={propFirmId}
            onChange={setPropFirmId}
            options={[{ key: "", label: "Tutte le firm" }, ...(propFirms.data ?? []).map((f) => ({ key: f.id, label: f.name }))]}
          />
          <input
            aria-label="Cerca cliente o ciclo"
            placeholder="Cerca cliente o ciclo…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`${inputClass} w-56 py-1.5`}
          />
        </div>

        {cycles.isLoading ? (
          <p className="text-sm text-muted">Caricamento…</p>
        ) : cycles.error ? (
          <ErrorNote>{errorMessage(cycles.error)}</ErrorNote>
        ) : (
          <div className="card">
            <DataTable
              columns={columns}
              rows={filtered}
              rowKey={(c) => c.id}
              onRowClick={(c) => navigate(`/cicli/${c.id}`)}
              empty={
                all.length === 0
                  ? {
                      title: "Nessun ciclo",
                      hint: "Un ciclo nasce con la prima fase di un cliente: apri la sua scheda e premi «Aggiungi conto», e comparirà qui con la sua fee e il suo recover.",
                      action: (
                        <Link to="/clienti" className="inline-flex rounded bg-accent px-3 py-1.5 text-sm font-medium text-on-accent hover:bg-accent/90">
                          Vai ai clienti
                        </Link>
                      ),
                    }
                  : { title: "Nessun ciclo con questi filtri", hint: "Cambia stato, firm o cerca un altro cliente." }
              }
            />
          </div>
        )}
      </div>

      {registraFor && (
        <NewPropAccountPanel
          open
          clientId={registraFor.client.id}
          clientName={registraFor.client.name}
          brokerAccounts={(groups.data?.find((g) => g.client.id === registraFor.client.id)?.accounts ?? []).filter((a) => a.role === "BROKER")}
          defaultTargetCycleId={registraFor.id}
          onClose={() => setRegistraFor(null)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

export function CycleDetail() {
  const isClient = useIsClient();
  const canWrite = useCanWrite();
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const cycle = useCycle(id);
  const groups = useAccountGroups();
  const [registraConto, setRegistraConto] = useState(false);

  if (cycle.isLoading) return <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>;
  if (cycle.error || !cycle.data) {
    return (
      <div className="px-6 py-6">
        <ErrorNote>{cycle.error ? errorMessage(cycle.error) : "Ciclo non trovato"}</ErrorNote>
        <Link to="/cicli" className="mt-3 inline-block text-sm text-muted hover:text-ink">
          Torna ai cicli
        </Link>
      </div>
    );
  }

  const c = cycle.data;
  const cur = c.currency;

  return (
    <>
      <PageHeader
        title={c.label ?? `${c.propFirm.name} ${c.preset.name}`}
        subtitle={`${c.client.name} · ${c.propFirm.name} ${money(c.accountSize, cur)} · ${PHASE_LABEL(c.currentPhase)} · ${
          c.closedAt ? `dal ${date(c.openedAt)} al ${date(c.closedAt)}, ${c.daysOpen} giorni` : `aperto da ${c.daysOpen} ${c.daysOpen === 1 ? "giorno" : "giorni"} (${date(c.openedAt)})`
        }`}
        actions={
          <>
            <StateBadge kind="cycle" state={c.state} />
            {/* La scheda cliente è dell'operatore: per il cliente è un 403. */}
            {!isClient && (
              <Link to={`/clienti/${c.client.id}`} className="text-sm text-muted hover:text-ink hover:underline">
                Scheda cliente
              </Link>
            )}
            {/* Il ciclo aspetta il conto prop della fase nuova: si sblocca da qui, non solo dalla scheda cliente. */}
            {!isClient && canWrite && c.awaitingPropPhase && (
              <Button variant="primary" size="sm" onClick={() => setRegistraConto(true)}>
                Registra il conto
              </Button>
            )}
            {/*
              * Un ciclo nato per errore si cancella, e solo a zero movimenti:
              * l'anteprima dice che cosa sparisce — il CID congelato per primo.
              */}
            {!isClient && <DeleteForever kind="ciclo" id={c.id} label="questo ciclo" size="sm" onDeleted={() => navigate("/cicli")} />}
          </>
        }
      />

      <div className="space-y-6 px-6 py-5">
        {/*
          * Quattro riquadri, e sono quattro perche' sono le quattro domande che
          * si fanno su un ciclo: quanto manca (o com'e' finito), quanto e'
          * costato, quanto va al cliente, quanto resta a noi.
          *
          * Tutto il resto — P&L sul reale, payout, rettifiche, netto voluto —
          * e' il DETTAGLIO di quei quattro, e sta nella riga sotto: erano
          * riquadri uguali agli altri, e otto numeri con lo stesso peso non
          * sono una schermata, sono un elenco.
          */}
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Su un ciclo chiuso il numero VIVO e' il netto; il recover diventa
              storia e lo dichiara. Un numero fermo che sembra vivo e' un difetto. */}
          {c.closedAt ? (
            <Box
              label="Netto del ciclo"
              value={c.cycleNet === null ? "—" : money(c.cycleNet, cur)}
              tone={c.cycleNet === null ? undefined : signClass(Number(c.cycleNet))}
              hint={
                /* «Di cui ribilanciamenti» arriva solo all'operatore: il cliente vede il netto che li include. */
                c.cycleNetRebalances != null && Number(c.cycleNetRebalances) !== 0
                  ? `Reale + payout − fee − rettifiche + ribilanciamenti, scritto alla chiusura · di cui ribilanciamenti ${money(c.cycleNetRebalances, cur)}`
                  : "Reale + payout − fee − rettifiche, scritto alla chiusura"
              }
            />
          ) : (
            <Box label={LABELS.terms.recoverAmount} value={money(c.recoverAmount, cur)} hint="Quanto serve recuperare sul conto reale" />
          )}
          <Box label={LABELS.terms.feesPaid} value={money(c.feesPaid, cur)} hint="Il prezzo d'acquisto del ciclo" />
          {c.propResets !== undefined && (
            <Box label="Reset del prop" value={String(c.propResets)} hint="Quante volte la prop ha azzerato il conto in questo ciclo, fee dei reset comprese in quelle pagate" />
          )}
          {/*
            * Le due quote vengono dal REGISTRO, non da una percentuale
            * applicata qui: sono gli importi che qualcuno ha confermato, e la
            * percentuale accanto e' quella congelata sul ciclo. Se un giorno
            * l'operatore ha spostato un importo, il riquadro mostra quello che
            * e' stato registrato — non quello che sarebbe venuto dal conto.
            */}
          <Box
            label="Quota cliente"
            value={money(c.split.client, c.split.currency)}
            hint={`${percent(c.burnSplitPct, 0)} alla bruciatura · ${percent(c.payoutSplitPct, 0)} sui payout`}
          />
          <Box label="Quota nostra" value={money(c.split.manager, c.split.currency)} hint="Quello che resta a noi, registrato" />
        </section>

        {/* Il dettaglio dei quattro, e il recover congelato quando il ciclo e'
            chiuso: non sparisce, smette di essere un titolo. */}
        <section className="card flex flex-wrap gap-x-6 gap-y-2 px-4 py-3 text-sm">
          <Detail label={LABELS.terms.realRealizedPnl} value={money(c.realRealizedPnl, cur)} tone={signClass(Number(c.realRealizedPnl))} />
          <Detail label="Payout incassati" value={money(c.payoutsReceived, cur)} />
          <Detail label={LABELS.terms.adjustments} value={money(c.adjustments, cur)} />
          {c.closedAt && (
            <Detail
              label={LABELS.terms.recoverAmount}
              value={money(c.recoverAmount, cur)}
              title="CONGELATO alla chiusura: è quanto restava da recuperare, non un obiettivo"
              tone="text-faint"
            />
          )}
          <Detail
            label="CID del ciclo"
            value={c.computerId ? <span className="machine-id">{c.computerId}</span> : <Dash />}
            title="Congelato all'apertura: i conti prop di questo ciclo si presentano così"
          />
        </section>

        {/*
          * I PERIODI: da dove vengono i numeri qui sopra.
          *
          * Non si mostra a un utente CLIENT, e non è una scelta estetica: la
          * rotta non è nella sua allowlist (`lib/client-scope.ts`), quindi per
          * lui sarebbe una chiamata che nasce 403.
          */}
        {!isClient && <PeriodsSection cycleId={c.id} canWrite={canWrite} />}

        {/* Anche i payout: stessa allowlist, stessa ragione dei periodi. */}
        {!isClient && <PayoutsSection cycleId={c.id} />}

        {/* I ribilanciamenti: righe inserite a mano, che il cliente non vede (vede il netto che le include). */}
        {!isClient && <RebalanceSection cycleId={c.id} canWrite={canWrite} />}

        <section>
          <h2 className="mb-2 text-sm font-medium text-ink">Fasi del ciclo</h2>
          {c.sessions.length === 0 ? (
            <p className="card px-4 py-4 text-sm text-muted">
              Nessuna fase: il ciclo esiste ma non è ancora andato a mercato.
            </p>
          ) : (
            <div className="card divide-y divide-line">
              {c.sessions.map((s) => (
                <div key={s.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm">
                  <span className="font-medium text-ink">{PHASE_LABEL(s.phase)}</span>
                  <StateBadge kind="session" state={s.state} />
                  <span className="text-muted">{s.canonicalSymbol}</span>
                  <span className="text-prop">{s.propAccount.label}</span>
                  {s.brokerAccount ? (
                    <span className="text-broker">{s.brokerAccount.label}</span>
                  ) : (
                    <span className="text-broker">in attesa del conto broker</span>
                  )}
                  <span className="text-muted">
                    {s.pairs} {s.pairs === 1 ? "operazione" : "operazioni"}
                  </span>
                  <span className="text-muted" title="Da recuperare all'inizio della fase">
                    obiettivo {money(s.profitTarget, cur)}
                  </span>
                  {/*
                    * Quanto ha lasciato sul reale QUESTA fase, ed e' il numero
                    * per cui esiste il recover: una fase superata lo lascia
                    * negativo, la bruciatura positivo.
                    *
                    * In valuta del CICLO, che e' quella in cui la chiusura l'ha
                    * scritto convertendolo: metterci accanto la valuta del
                    * conto broker — com'era — diceva «€ 636» su 636 dollari.
                    */}
                  <span className={signClass(Number(s.costsRecovered))} title={`${LABELS.terms.costsRecovered} in questa fase`}>
                    {money(s.costsRecovered, cur)}
                  </span>
                  <span className="text-faint">
                    {date(s.startedAt)}
                    {s.completedAt ? ` → ${date(s.completedAt)}` : ""}
                  </span>
                  {/* Le fasi sono la pagina di chi opera: il cliente vede l'esito, non la manovra. */}
                  {!isClient && (
                    <Link to={`/fasi/${s.id}`} className="ml-auto text-muted hover:text-ink hover:underline">
                      Apri la fase
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {registraConto && (
        <NewPropAccountPanel
          open
          clientId={c.client.id}
          clientName={c.client.name}
          brokerAccounts={(groups.data?.find((g) => g.client.id === c.client.id)?.accounts ?? []).filter((a) => a.role === "BROKER")}
          defaultTargetCycleId={c.id}
          onClose={() => setRegistraConto(false)}
        />
      )}
    </>
  );
}

/** Un numero della riga di dettaglio: etichetta piccola, valore accanto. */
function Detail({ label, value, tone, title }: { label: string; value: React.ReactNode; tone?: string; title?: string }) {
  return (
    <span className="flex items-baseline gap-1.5" title={title}>
      <span className="text-xs text-faint">{label}</span>
      <span className={tone ?? "text-ink"}>{value}</span>
    </span>
  );
}

function Box({ label, value, hint, tone }: { label: string; value: React.ReactNode; hint?: string; tone?: string }) {
  return (
    <div className="card p-4">
      <p className="text-xs text-faint">{label}</p>
      <p className={`mt-1 text-lg font-medium ${tone ?? "text-ink"}`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-faint">{hint}</p>}
    </div>
  );
}
